import { api } from './client';
import { API_FETCH_URL } from './base';

const BASE = '/admin/play-live';
const BASE_URL = API_FETCH_URL;

export type LiveStatus = 'requested' | 'starting' | 'live' | 'closed';

// A Play account's browser profile opened on the VPS for a person to drive.
export interface LiveSession {
  id: string;
  // One of the two: a Play account (First publication) or a browser profile.
  account_id: number | null;
  profile_id: number | null;
  account_name: string;
  google_email: string | null;
  admin_email: string | null;
  status: LiveStatus;
  // What the service is doing while it is not live yet (waiting for the runner…).
  note: string | null;
  close_reason: string | null;
  requested_at: number;
  started_at: number | null;
  last_seen_at: number | null;
  closed_at: number | null;
}

// Play accounts with a browser profile, as the Browser Profiles page lists them.
export interface LiveAccount {
  id: number;
  name: string;
  google_email: string | null;
  shardx_profile_id: string;
  session_status: string;
}

export const listLiveAccounts = () => api.get<LiveAccount[]>(`${BASE}/accounts`);
export const listLiveSessions = () => api.get<LiveSession[]>(`${BASE}/sessions`);
export const openLiveSession = (accountId: number) => api.post<LiveSession>(`${BASE}/sessions`, { account_id: accountId });
export const openProfileLiveSession = (profileId: number) => api.post<LiveSession>(`${BASE}/sessions`, { profile_id: profileId });
export const getLiveSession = (id: string) => api.get<LiveSession>(`${BASE}/sessions/${id}`);
export const closeLiveSession = (id: string) => api.post<{ id: string; closed: boolean }>(`${BASE}/sessions/${id}/close`);
export const liveTicket = (id: string) => api.post<{ ticket: string; exp: number }>(`${BASE}/sessions/${id}/ticket`);

// A file of a live browser session, kept 7 days: one a site downloaded
// ('down') or one a person uploaded for a site's file input ('up').
export interface LiveDownload {
  id: string;
  name: string;
  size: number;
  content_type: string | null;
  direction: 'down' | 'up';
  created_at: number;
}

export const listLiveDownloads = (id: string) => api.get<LiveDownload[]>(`${BASE}/sessions/${id}/downloads`);

/** Same cap as the worker's (MAX_LIVE_DOWNLOAD_BYTES): checked here so a big file fails before it is sent. */
export const MAX_LIVE_UPLOAD_BYTES = 95 * 1024 * 1024;

/** A file from this computer for a site that asked for one; the viewer then names it to the service. */
export const uploadLiveFile = (id: string, file: File) =>
  api.upload<LiveDownload>(`${BASE}/sessions/${id}/uploads?name=${encodeURIComponent(file.name)}`, file, file.type || 'application/octet-stream');

/** Fetches the file with the admin token and hands it to this browser's own Save. */
export async function saveLiveDownload(sessionId: string, file: LiveDownload): Promise<void> {
  const blob = await api.blob(`${BASE}/sessions/${sessionId}/downloads/${file.id}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The viewer socket on the same API host the rest of the dashboard talks to. */
export function liveSocketUrl(id: string, ticket: string): string {
  const base = new URL(BASE_URL, window.location.href);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${base.toString().replace(/\/$/, '')}${BASE}/sessions/${id}/ws?ticket=${encodeURIComponent(ticket)}`;
}
